import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Users, Search, Ban, CheckCircle2, Gift, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('created_at');
  const [loading, setLoading] = useState(true);
  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUsers({ search: query, page, page_size: pageSize, sort });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [query, page, sort]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (u) => {
    try {
      await adminAPI.setUserStatus(u._id, !u.is_active);
      toast.success(u.is_active ? `${u.nickname} disabled` : `${u.nickname} enabled`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const [xpTarget, setXpTarget] = useState(null);
  const [xpAmount, setXpAmount] = useState('100');

  const bonusXp = async (u) => {
    const amount = parseInt(xpAmount, 10);
    if (!amount) { toast.error('Enter a non-zero number'); return; }
    try {
      const res = await adminAPI.grantBonusXp(u._id, amount);
      toast.success(`${u.nickname}: new total ${res.data.xp_total} XP`);
      setXpTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Space_Grotesk'] flex items-center gap-3">
            <Users className="w-7 h-7 text-teal-400" /> Users
          </h1>
          <p className="text-sm text-slate-400 mt-1">{total} registered • search, disable accounts, grant bonus XP.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setPage(1); setQuery(search); } }}
              placeholder="Email, nickname or ref code…"
              className="pl-9 w-64 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-600"
            />
          </div>
          <select value={sort} onChange={e => { setPage(1); setSort(e.target.value); }}
            className="bg-slate-900/60 border border-slate-700 text-slate-300 rounded-md px-2 text-sm">
            <option value="created_at">Newest</option>
            <option value="xp_total">Top XP</option>
            <option value="xp_week">Weekly XP</option>
            <option value="last_active_date">Last active</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner className="py-16" />
      ) : (
        <div className="bg-[#0d1f35] border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-800 text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">User</th>
                  <th className="px-3 py-3">XP</th>
                  <th className="px-3 py-3">Week</th>
                  <th className="px-3 py-3">Quizzes</th>
                  <th className="px-3 py-3">Refs</th>
                  <th className="px-3 py-3">Joined</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                    <td className="px-5 py-3">
                      <p className="text-white font-medium">{u.nickname} {u.role === 'admin' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 ml-1">ADMIN</span>}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </td>
                    <td className="px-3 py-3 text-teal-300 font-['Azeret_Mono']">{u.xp_total}</td>
                    <td className="px-3 py-3 text-slate-300 font-['Azeret_Mono']">{u.xp_week}</td>
                    <td className="px-3 py-3 text-slate-300">{u.quizzes_played}</td>
                    <td className="px-3 py-3 text-slate-300">{u.referral_count}</td>
                    <td className="px-3 py-3 text-slate-400 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-3">
                      {u.is_active
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">active</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300">disabled</span>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1.5 justify-end items-center">
                        {xpTarget === u._id ? (
                          <div className="flex gap-1.5 items-center">
                            <Input value={xpAmount} onChange={e => setXpAmount(e.target.value)}
                              className="h-8 w-20 bg-slate-900/60 border-slate-700 text-white text-sm" />
                            <Button size="sm" onClick={() => bonusXp(u)}
                              className="h-8 px-2 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold">+XP</Button>
                            <Button size="sm" variant="outline" onClick={() => setXpTarget(null)}
                              className="h-8 px-2 border-slate-700 text-slate-400">✕</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => { setXpTarget(u._id); setXpAmount('100'); }} title="Grant bonus XP"
                            className="h-8 px-2 border-slate-700 text-slate-300 hover:bg-slate-800">
                            <Gift className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {u.role !== 'admin' && (
                          <Button size="sm" variant="outline" onClick={() => toggleStatus(u)}
                            title={u.is_active ? 'Disable account' : 'Enable account'}
                            className={`h-8 px-2 border-slate-700 hover:bg-slate-800 ${u.is_active ? 'text-rose-400' : 'text-emerald-400'}`}>
                            {u.is_active ? <Ban className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">No users match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-800">
              <span className="text-xs text-slate-500">Page {page} of {pages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="h-8 border-slate-700 text-slate-300"><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)}
                  className="h-8 border-slate-700 text-slate-300"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
